import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ContactOriginApiService } from '../../contact-origins/services/contact-origin-api.service';
import { ContactStateApiService } from '../../contact-states/services/contact-state-api.service';
import { ContactCycleApiService } from '../../contact-cycles/services/contact-cycle-api.service';
import { TicketsConfigApiService } from '../../tickets/services/tickets-config-api.service';
import {
  FormListModel,
  FormDetailModel,
  FormCreatePayload,
  FormUpdatePayload,
  FormSettingModel,
  FormPageParams,
} from '../models/form.model';
import { PageResponse } from '../../audit/models/audit.model';
import { FormFieldModel, FormFieldOption, FormFieldCrmMap } from '../models/form-field.model';
import { FormStatsDailyModel, FormFieldDropoffModel } from '../models/form-metrics.model';
import { MktFormTargetUrl, MktFormTargetUrlRequest } from '../models/target-url.model';
import { MktFormSubmission, MktFormSubmissionFieldValue, MktSubmissionPageParams } from '../models/submission.model';
import { MktFormNote, MktFormNoteRequest } from '../models/form-note.model';
import { FormStatus } from '../models/form.model';
import { CrmObjectType, CrmUpdateRule } from '../models/form-field.model';
import { AuthService } from '../../auth-pages/services/auth.service';

/** Respuesta de GET .../crm-map/suggest (inc19). No persiste. */
export interface CrmMapSuggestion {
  formFieldId: number;
  matched: boolean;
  crmObjectType?: CrmObjectType | null;
  isCustomAttribute: boolean;
  crmStandardField?: string | null;
  updateRule?: CrmUpdateRule | null;
  reason?: string;
}

/** Definición de atributo EAV para el selector por nombre del mapeo CRM (inc19). */
export interface CrmAttributeDefinition {
  id: number;
  name: string;
  type?: string;
  objectType: CrmObjectType;
}

/**
 * Campo ESTÁNDAR mapeable curado por objeto CRM (crm-standard-fields).
 * `key` = nombre EXACTO de columna de BD → se guarda en crmStandardField.
 * `label` = etiqueta legible en español para el dropdown.
 * `type` = pista semántica (TEXT | EMAIL | PHONE | NUMBER | URL).
 * `required` = true si esta columna es obligatoria para crear el objeto CRM
 *   (CONTACT→mail, COMPANY→commercial_name, DEAL→name). LEAD no tiene obligatorio.
 */
export interface CrmStandardField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  /**
   * Identificador del catálogo (FK) al que apunta esta propiedad, o null si es un
   * campo de texto/valor libre. Ej. `id_origin` → `'contact_origin'`. Cuando NO es
   * null, el "valor fijo" del campo debe elegirse de una lista (dropdown) en vez de
   * un input de texto libre.
   */
  catalog?: string | null;
}

/**
 * Opción normalizada de un catálogo (FK) para poblar el dropdown del "valor fijo".
 * `value` = lo que se persiste en defaultFixedValue (code||name; el backend lo
 * resuelve por code→name→crear). `label` = etiqueta legible (name).
 */
export interface CatalogOption {
  value: string;
  label: string;
  code: string;
  name: string;
  id: number;
}

@Injectable({ providedIn: 'root' })
export class MarketingFormsService {
  private readonly base = `${environment.apiUrl}api/marketing-forms`;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private contactOriginApi: ContactOriginApiService,
    private contactStateApi: ContactStateApiService,
    private contactCycleApi: ContactCycleApiService,
    private ticketsConfigApi: TicketsConfigApiService,
  ) {}

  // ── Forms ──────────────────────────────────────────────────────────────────

  getForms(params: FormPageParams = {}): Observable<PageResponse<FormListModel>> {
    let httpParams = new HttpParams();
    if (params.status)     httpParams = httpParams.set('status',     params.status);
    if (params.formType)   httpParams = httpParams.set('formType',   params.formType);
    if (params.isTemplate != null) httpParams = httpParams.set('isTemplate', String(params.isTemplate));
    if (params.searchName) httpParams = httpParams.set('searchName', params.searchName);
    if (params.page  != null) httpParams = httpParams.set('page',  params.page);
    if (params.size  != null) httpParams = httpParams.set('size',  params.size);
    return this.http.get<PageResponse<FormListModel>>(this.base, { params: httpParams });
  }

  getFormById(id: number): Observable<FormDetailModel> {
    return this.http.get<FormDetailModel>(`${this.base}/${id}`);
  }

  getFormBySlug(slug: string): Observable<FormDetailModel> {
    return this.http.get<FormDetailModel>(`${this.base}/slug/${slug}`);
  }

  createForm(payload: FormCreatePayload): Observable<FormDetailModel> {
    return this.http.post<FormDetailModel>(this.base, payload);
  }

  updateForm(id: number, payload: FormUpdatePayload): Observable<FormDetailModel> {
    return this.http.put<FormDetailModel>(`${this.base}/${id}`, payload);
  }

  deleteForm(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  duplicateForm(id: number): Observable<FormDetailModel> {
    return this.http.post<FormDetailModel>(`${this.base}/${id}/duplicate`, {});
  }

  publishForm(id: number): Observable<FormDetailModel> {
    return this.updateForm(id, { status: 'PUBLISHED' });
  }

  archiveForm(id: number): Observable<FormDetailModel> {
    return this.updateForm(id, { status: 'ARCHIVED' });
  }

  pauseForm(id: number): Observable<FormDetailModel> {
    return this.updateForm(id, { status: 'PAUSED' });
  }

  // inc27: reanudar un formulario pausado → vuelve a estar publicado.
  resumeForm(id: number): Observable<FormDetailModel> {
    return this.updateForm(id, { status: 'PUBLISHED' });
  }

  // inc27: restaurar un formulario archivado → vuelve a borrador.
  restoreForm(id: number): Observable<FormDetailModel> {
    return this.updateForm(id, { status: 'DRAFT' });
  }

  setStatus(id: number, status: FormStatus): Observable<FormDetailModel> {
    return this.updateForm(id, { status });
  }

  // ── Notas internas (inc29) ───────────────────────────────────────────────────

  getNotes(formId: number): Observable<MktFormNote[]> {
    return this.http.get<MktFormNote[]>(`${this.base}/${formId}/notes`);
  }

  createNote(formId: number, payload: MktFormNoteRequest): Observable<MktFormNote> {
    return this.http.post<MktFormNote>(`${this.base}/${formId}/notes`, payload);
  }

  deleteNote(formId: number, noteId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${formId}/notes/${noteId}`);
  }

  // ── Settings ───────────────────────────────────────────────────────────────

  getSettings(formId: number): Observable<FormSettingModel> {
    return this.http.get<FormSettingModel>(`${this.base}/${formId}/settings`);
  }

  createSettings(formId: number, payload: Partial<FormSettingModel> = {}): Observable<FormSettingModel> {
    return this.http.post<FormSettingModel>(`${this.base}/${formId}/settings`, payload);
  }

  updateSettings(formId: number, payload: Partial<FormSettingModel>): Observable<FormSettingModel> {
    return this.http.put<FormSettingModel>(`${this.base}/${formId}/settings`, payload);
  }

  // ── Fields ─────────────────────────────────────────────────────────────────

  getFields(formId: number): Observable<FormFieldModel[]> {
    return this.http.get<FormFieldModel[]>(`${this.base}/${formId}/fields`);
  }

  createField(formId: number, field: Partial<FormFieldModel>): Observable<FormFieldModel> {
    return this.http.post<FormFieldModel>(`${this.base}/${formId}/fields`, this.toFieldSaveRequest(field));
  }

  updateField(formId: number, fieldId: number, field: Partial<FormFieldModel>): Observable<FormFieldModel> {
    return this.http.put<FormFieldModel>(`${this.base}/${formId}/fields/${fieldId}`, this.toFieldSaveRequest(field));
  }

  deleteField(formId: number, fieldId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${formId}/fields/${fieldId}`);
  }

  // ── Field Options ──────────────────────────────────────────────────────────

  getFieldOptions(formId: number, fieldId: number): Observable<FormFieldOption[]> {
    return this.http.get<FormFieldOption[]>(`${this.base}/${formId}/fields/${fieldId}/options`);
  }

  createFieldOption(formId: number, fieldId: number, option: Partial<FormFieldOption>): Observable<FormFieldOption> {
    return this.http.post<FormFieldOption>(`${this.base}/${formId}/fields/${fieldId}/options`, option);
  }

  updateFieldOption(
    formId: number,
    fieldId: number,
    optionId: number,
    option: Partial<FormFieldOption>,
  ): Observable<FormFieldOption> {
    return this.http.put<FormFieldOption>(`${this.base}/${formId}/fields/${fieldId}/options/${optionId}`, option);
  }

  deleteFieldOption(formId: number, fieldId: number, optionId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${formId}/fields/${fieldId}/options/${optionId}`);
  }

  // ── Field CRM Mapping ──────────────────────────────────────────────────────

  getFieldCrmMap(formId: number, fieldId: number): Observable<FormFieldCrmMap | null> {
    return this.http.get<FormFieldCrmMap[]>(`${this.base}/${formId}/fields/${fieldId}/crm-map`).pipe(
      map(arr => arr?.length ? arr[0] : null),
    );
  }

  saveFieldCrmMap(formId: number, fieldId: number, mapping: FormFieldCrmMap): Observable<FormFieldCrmMap> {
    return this.http.put<FormFieldCrmMap>(`${this.base}/${formId}/fields/${fieldId}/crm-map`, mapping);
  }

  deleteFieldCrmMap(formId: number, fieldId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${formId}/fields/${fieldId}/crm-map`);
  }

  // inc19: auto-sugerencia de mapeo por campo (heurística en backend, no persiste).
  suggestFieldCrmMap(formId: number, fieldId: number): Observable<CrmMapSuggestion> {
    return this.http.get<CrmMapSuggestion>(`${this.base}/${formId}/fields/${fieldId}/crm-map/suggest`);
  }

  // inc19: catálogo de atributos personalizados (EAV) por tipo de objeto CRM, para
  // el selector por nombre del mapeo. Filtra por businessId del token; array plano
  // (no paginado). COMPANY/LEAD no tienen EAV → devuelven [].
  getCrmAttributeDefinitions(objectType: CrmObjectType, search?: string): Observable<CrmAttributeDefinition[]> {
    let params = new HttpParams().set('objectType', objectType);
    if (search && search.trim()) params = params.set('search', search.trim());
    return this.http.get<CrmAttributeDefinition[]>(`${this.base}/crm-attribute-definitions`, { params });
  }

  // Lista curada de campos ESTÁNDAR mapeables por objeto CRM, para el dropdown del
  // mapeo. Array plano (NO paginado). El backend filtra por businessId del token;
  // objectType no soportado (p. ej. TICKET) devuelve [] (no 400).
  getCrmStandardFields(objectType: CrmObjectType): Observable<CrmStandardField[]> {
    const businessId = this.authService.getCurrentUser()?.businessId ?? 1;
    const params = new HttpParams()
      .set('businessId', String(businessId))
      .set('objectType', objectType);
    return this.http.get<CrmStandardField[]>(`${this.base}/crm-standard-fields`, { params });
  }

  // ── Catálogos (FK) para el "valor fijo" de un campo mapeado ──────────────────

  // Devuelve las opciones normalizadas del catálogo indicado por el descriptor
  // `catalog` de un campo estándar. Catálogos soportados:
  //   - `contact_origin`           → GET /api/contact-origins     (code → value=code||name)
  //   - `contact_state`            → GET /api/contact-states      (sin code → value=name)
  //   - `contact_cycle`            → GET /api/contact-cycles      (sin code → value=name)
  //   - `ticket_priority`          → GET /api/ticket-priorities   (name interno → value=name)
  //   - `ticket_category`          → GET /api/ticket-categories   (value=name, label=name)
  //   - `company_tax_type`         → GET …/crm-standard-fields/catalog-values?catalog=…
  //   - `company_relationship_type`→ idem
  //   - `company_size`             → idem
  //   - `company_sector`           → idem (incluye globales; backend prioriza tenant)
  //   - `company_state_lead`       → idem
  //   - `company_cycle`            → idem (solo ciclos activos)
  //   - `lead_cycle`               → idem (solo ciclos activos)
  //   - `lead_state`               → idem (solo estados activos)
  // Un catálogo desconocido devuelve [] (la UI degrada a input de texto).
  getCatalogOptions(catalog: string): Observable<CatalogOption[]> {
    const businessId = this.authService.getCurrentUser()?.businessId ?? 1;
    switch (catalog) {
      case 'contact_origin':
        return this.contactOriginApi.getOrigins(businessId, 0, 500).pipe(
          map(list => (list ?? []).map(o => ({
            value: o.code || o.name,
            label: o.name,
            code: o.code,
            name: o.name,
            id: o.id,
          }))),
        );
      case 'contact_state':
        return this.contactStateApi.getStates(businessId, 0, 500).pipe(
          map(list => (list ?? []).map(s => ({
            value: s.name,
            label: s.name,
            code: '',
            name: s.name,
            id: s.id,
          }))),
        );
      case 'contact_cycle':
        return this.contactCycleApi.getCycles(businessId, 0, 500).pipe(
          map(list => (list ?? []).map(c => ({
            value: c.name,
            label: c.name,
            code: '',
            name: c.name,
            id: c.id,
          }))),
        );
      case 'ticket_priority':
        // name = identificador interno (URGENT/HIGH/MEDIUM/LOW); label = texto visible.
        // El backend resuelve idTicketPriority por name al procesar la submission.
        return this.ticketsConfigApi.getPriorities().pipe(
          map(list => (list ?? []).filter(p => p.isActive).map(p => ({
            value: p.name,
            label: p.label,
            code:  p.name,
            name:  p.label,
            id:    p.id,
          }))),
        );
      case 'ticket_category':
        // Incluye todas las categorías activas (padre e hija). value = name para
        // que el backend resuelva idTicketCategory por nombre.
        return this.ticketsConfigApi.getCategories().pipe(
          map(list => (list ?? []).filter(c => c.isActive).map(c => ({
            value: c.name,
            label: c.name,
            code:  c.name,
            name:  c.name,
            id:    c.id,
          }))),
        );
      // Catálogos de Company y Lead: endpoint unificado /catalog-values.
      case 'company_tax_type':
      case 'company_relationship_type':
      case 'company_size':
      case 'company_sector':
      case 'company_state_lead':
      case 'company_cycle':
      case 'lead_cycle':
      case 'lead_state':
        return this.getCatalogFromApi(catalog);
      default:
        return of([]);
    }
  }

  // Llama al endpoint unificado catalog-values para catálogos de Company/Lead.
  // El backend filtra por businessId del token JWT; no se pasa aquí.
  private getCatalogFromApi(catalog: string): Observable<CatalogOption[]> {
    const params = new HttpParams().set('catalog', catalog);
    return this.http
      .get<{ id: number; name: string }[]>(
        `${this.base}/crm-standard-fields/catalog-values`,
        { params },
      )
      .pipe(
        map(list => (list ?? []).map(item => ({
          value: item.name,
          label: item.name,
          code:  '',
          name:  item.name,
          id:    item.id,
        }))),
      );
  }

  // ── Target URLs ────────────────────────────────────────────────────────────

  getTargetUrls(formId: number): Observable<MktFormTargetUrl[]> {
    return this.http.get<MktFormTargetUrl[]>(`${this.base}/${formId}/target-urls`);
  }

  createTargetUrl(formId: number, payload: MktFormTargetUrlRequest): Observable<MktFormTargetUrl> {
    return this.http.post<MktFormTargetUrl>(`${this.base}/${formId}/target-urls`, payload);
  }

  updateTargetUrl(formId: number, urlId: number, payload: MktFormTargetUrlRequest): Observable<MktFormTargetUrl> {
    return this.http.put<MktFormTargetUrl>(`${this.base}/${formId}/target-urls/${urlId}`, payload);
  }

  deleteTargetUrl(formId: number, urlId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${formId}/target-urls/${urlId}`);
  }

  // ── Submissions ────────────────────────────────────────────────────────────

  getSubmissions(formId: number, params: MktSubmissionPageParams = {}): Observable<PageResponse<MktFormSubmission>> {
    let httpParams = new HttpParams();
    if (params.status)                httpParams = httpParams.set('status',      params.status);
    if (params.onlyPartial != null)   httpParams = httpParams.set('onlyPartial', String(params.onlyPartial));
    if (params.dateFrom)              httpParams = httpParams.set('dateFrom',    params.dateFrom);
    if (params.dateTo)                httpParams = httpParams.set('dateTo',      params.dateTo);
    if (params.search)                httpParams = httpParams.set('search',      params.search);
    if (params.contactId != null)     httpParams = httpParams.set('contactId',   params.contactId);
    if (params.page  != null)         httpParams = httpParams.set('page',        params.page);
    if (params.size  != null)         httpParams = httpParams.set('size',        params.size);
    return this.http.get<PageResponse<MktFormSubmission>>(`${this.base}/${formId}/submissions`, { params: httpParams });
  }

  getSubmission(formId: number, submissionId: number): Observable<MktFormSubmission> {
    return this.http.get<MktFormSubmission>(`${this.base}/${formId}/submissions/${submissionId}`);
  }

  // El path NO incluye {formId} — coincide con el backend.
  getSubmissionFields(submissionId: number): Observable<MktFormSubmissionFieldValue[]> {
    return this.http.get<MktFormSubmissionFieldValue[]>(`${this.base}/submissions/${submissionId}/fields`);
  }

  // ── Analytics ──────────────────────────────────────────────────────────────

  getStatsDaily(formId: number): Observable<FormStatsDailyModel[]> {
    return this.http.get<FormStatsDailyModel[]>(`${this.base}/${formId}/analytics/stats-daily`);
  }

  refreshStatsToday(formId: number): Observable<void> {
    return this.http.post<void>(`${this.base}/${formId}/analytics/stats-daily/refresh-today`, null);
  }

  getFieldDropoffs(formId: number): Observable<FormFieldDropoffModel[]> {
    return this.http.get<FormFieldDropoffModel[]>(`${this.base}/${formId}/analytics/field-dropoffs`);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  // El backend define MktFormFieldSaveRequest sin options/crmMap; los filtramos
  // para no enviar payload basura. La sincronización de options/crm-map debe
  // hacerse contra sus endpoints específicos.
  private toFieldSaveRequest(field: Partial<FormFieldModel>): Partial<FormFieldModel> {
    const { id, formId, datetimeCreated, datetimeUpdated, options, crmMap, ...payload } = field;
    return payload;
  }
}
